import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import { AlertCircle, X } from "lucide-react";
import { useCallback, useEffect } from "react";
import {
	useDocuSignActions,
	useIsModalOpen,
	useSigningError,
	useSigningUrl,
} from "../../stores/docusign.store";

export const DocusignSignatureModal = () => {
	const isOpen = useIsModalOpen();
	const signingUrl = useSigningUrl();
	const error = useSigningError();
	const { closeSigningModal, clearError } = useDocuSignActions();

	const handleClose = useCallback(() => {
		closeSigningModal();
		clearError();
	}, [closeSigningModal, clearError]);

	const handleIframeMessage = useCallback(
		(event: MessageEvent) => {
			if (!signingUrl || event.origin !== new URL(signingUrl).origin) {
				return;
			}

			if (event.data.type === "docusign_signing_complete") {
				handleClose();
			}
		},
		[signingUrl, handleClose],
	);

	useEffect(() => {
		if (isOpen && signingUrl) {
			window.addEventListener("message", handleIframeMessage);
			return () => {
				window.removeEventListener("message", handleIframeMessage);
			};
		}
	}, [isOpen, signingUrl, handleIframeMessage]);

	if (error) {
		return (
			<Dialog open={!!error} onOpenChange={() => clearError()}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2 text-red-600">
							<AlertCircle size={20} />
							Erreur de signature
						</DialogTitle>
					</DialogHeader>
					<div className="py-4">
						<p className="text-gray-700">{error}</p>
					</div>
					<div className="flex justify-end">
						<Button onClick={() => clearError()}>Fermer</Button>
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="max-w-5xl w-5xl max-h-[90vh] p-0">
				<DialogHeader className="px-6 py-4 border-b">
					<div className="flex items-center justify-between">
						<DialogTitle>Signature du document</DialogTitle>
						<Button
							variant="ghost"
							size="icon"
							onClick={handleClose}
							className="h-6 w-6"
						>
							<X size={16} />
						</Button>
					</div>
				</DialogHeader>
				<div className="h-[80vh]">
					{signingUrl && (
						<iframe
							src={signingUrl}
							title="DocuSign Signature"
							className="w-full h-full border-0"
							sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
						/>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};
